<template>
    <!-- Error fallback -->
    <div
        v-if="error"
        id="wisp-preloader-error"
        class="page-surface text-primary-200 fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-4 px-4 text-center"
        role="alert"
    >
        <div class="text-danger-400 text-2xl">
            <fa :icon="['fas', 'triangle-exclamation']" />
        </div>
        <p class="text-primary-200 max-w-md text-sm">{{ message }}</p>
    </div>

    <!-- Auth routes: flat cover, no skeleton -->
    <div v-else-if="isAuthRoute" id="wisp-preloader-auth" class="page-surface fixed inset-0 z-[9999]" aria-busy="true" />

    <!-- App shell skeleton -->
    <div v-else id="wisp-preloader" class="page-surface animate-in fade-in-0 fixed inset-0 z-[9999] flex duration-200" aria-busy="true">
        <aside id="wisp-preloader-sidebar" class="bg-primary-800 hidden h-screen w-64 shrink-0 flex-col py-2 backdrop-blur-sm md:flex">
            <div id="wisp-preloader-sidebar-header" class="flex items-center px-3 py-2">
                <Shimmer class="h-8 w-28 rounded" />
            </div>

            <section class="flex flex-1 flex-col overflow-hidden">
                <div id="wisp-preloader-server-widget" class="mx-3 my-2 p-3">
                    <div class="flex items-center gap-3">
                        <Shimmer class="h-9 w-9 shrink-0 rounded" />
                        <div class="min-w-0 flex-1 space-y-2">
                            <Shimmer class="h-3 w-3/4 rounded" />
                            <Shimmer class="h-2 w-1/2 rounded" />
                        </div>
                    </div>
                    <div class="mt-3 grid grid-cols-3 gap-2">
                        <Shimmer class="h-2 rounded" />
                        <Shimmer class="h-2 rounded" />
                        <Shimmer class="h-2 rounded" />
                    </div>
                </div>

                <nav id="wisp-preloader-nav" class="flex flex-1 flex-col gap-0.5 px-2 py-2">
                    <div v-for="(width, i) in navItems" :key="i" class="flex h-9 items-center gap-3 rounded px-3">
                        <Shimmer class="h-4 w-4 shrink-0 rounded" />
                        <Shimmer class="h-3 rounded" :style="{ width }" />
                    </div>
                </nav>

                <div id="wisp-preloader-sidebar-footer" class="border-primary-400/20 mt-auto border-t px-2 py-2">
                    <div class="page-surface/30 ring-primary-400/20 rounded ring-1">
                        <div id="wisp-preloader-sidebar-actions" class="flex items-center justify-center gap-6 px-3 pb-3 pt-2">
                            <Shimmer class="h-6 w-6 rounded" />
                            <Shimmer class="h-6 w-6 rounded" />
                            <Shimmer class="h-6 w-6 rounded" />
                        </div>
                        <div id="wisp-preloader-user" class="flex items-center gap-3 px-3 pb-2">
                            <Shimmer class="ring-primary-400/20 h-9 w-9 shrink-0 rounded-full ring-2" />
                            <div class="min-w-0 flex-1 space-y-1.5">
                                <Shimmer class="h-3 w-2/3 rounded" />
                                <Shimmer class="h-2 w-1/2 rounded" />
                            </div>
                            <Shimmer class="h-7 w-7 rounded" />
                        </div>
                    </div>
                </div>
            </section>
        </aside>

        <main id="wisp-preloader-main" class="flex-1 overflow-hidden px-4 pt-16 md:px-8 md:pt-8">
            <header id="wisp-preloader-header" class="mb-8 flex items-center gap-3">
                <div
                    id="wisp-preloader-header-icon"
                    class="bg-accent-500/15 flex h-14 w-14 shrink-0 items-center justify-center rounded-full md:h-20 md:w-20"
                >
                    <Shimmer class="h-6 w-6 rounded md:h-8 md:w-8" />
                </div>
                <div class="min-w-0 flex-1 space-y-2">
                    <Shimmer class="h-6 w-48 rounded md:h-8 md:w-72" />
                    <Shimmer class="h-3 w-32 rounded md:w-56" />
                </div>
                <div id="wisp-preloader-breadcrumbs" class="hidden xl:flex xl:items-center xl:gap-3">
                    <Shimmer class="h-3 w-16 rounded" />
                    <Shimmer class="h-3 w-20 rounded" />
                    <Shimmer class="h-3 w-24 rounded" />
                </div>
            </header>

            <Shimmer id="wisp-preloader-content" class="h-[clamp(20rem,60vh,40rem)] rounded" />
        </main>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Shimmer from '~/components/Shimmer.vue';

defineProps<{
    message: string;
    error?: boolean;
}>();

const isAuthRoute = computed(() => {
    if (typeof window === 'undefined') return false;
    const p = window.location.pathname;
    return p.startsWith('/login') || p.startsWith('/auth') || p.startsWith('/password');
});

const navItems = ['72%', '58%', '80%', '50%', '68%', '54%', '76%', '62%'];
</script>
